import React, { useContext, useEffect, useMemo, useCallback } from "react";
import { HolderOutlined, DeleteOutlined } from "@ant-design/icons";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Row, Input, Select, FormInstance, Divider, Col } from "antd";
import { useTranslation } from "react-i18next";
import { getUuid } from "@src/helpers/functions";
import { questionTypeOptions } from "@src/helpers/constants";
import {
  SHORT_QUESTION_MAX_LENGTH,
  capShortQuestion,
  truncateToShortQuestion,
} from "@src/helpers/report";
import { startCase } from "lodash";
import { useDispatch } from "react-redux";
import { AppDispatch, dynamicSet, useDynamicSelector } from "@src/redux/index";
import { IoMdAddCircle } from "react-icons/io";
import { ParentQuestion } from "./QuestionComponent";

interface Props {
  form: FormInstance;
  record: any;
}

export interface Question {
  id: string;
  name?: string;
  /**
   * Admin-authored column header used by the Report page and its Excel
   * export. Capped at SHORT_QUESTION_MAX_LENGTH; left blank it falls back to
   * the truncated question name.
   */
  shortQuestion?: string;
  type?: string;
  order?: number;
  optionList?: string[];
  key: string;
  parentId?: string;
  fieldType: string;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = React.memo(() => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
});

interface SortableRowProps {
  "data-row-key": string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = React.memo((props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <Row
        ref={setNodeRef}
        style={style}
        {...attributes}
        align="middle"
        gutter={16}
      >
        {props.children}
      </Row>
    </RowContext.Provider>
  );
});

const CustomQuestion: React.FC<Props> = ({ form, record }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const dataSource = useDynamicSelector("data_source");

  // const data_source = useDynamicSelector(`question_${record?.id}`) || [];

  // useEffect(() => {
  //   if (record?.questioncustom_test_list.length > 0) {
  //     dispatch(dynamicSet(`question_${record?.id}`, record.question));
  //   }
  // }, [record, dispatch]);

  const handleAdd = useCallback(() => {
    const newRow: Question = {
      key: (record?.customTestList?.length + 1).toString(),
      name: ``,
      id: getUuid(),
      parentId: record?.id,
      fieldType: "select",
    };

    const updatedDataSource =
      record?.customTestList?.length > 0
        ? [...record?.customTestList, newRow]
        : [newRow];
    let reAssignValues = dataSource.map((list: ParentQuestion) => {
      if (list.id.includes(record?.id)) {
        return {
          ...list,
          customTestList: updatedDataSource,
        };
      }
      return list;
    });
    dispatch(dynamicSet(`data_source`, reAssignValues));
  }, [dispatch, dataSource, record]);

  const handleRemove = useCallback(
    (key: string,id: string) => {
      // const updated_data = data_source.filter(
      //   (item: Question) => item.key !== key
      // );
      let reAssignValues = dataSource.map((list: ParentQuestion) => {
        if (list.id.includes(record?.id)) {
          return {
            ...list,
            customTestList: list?.customTestList?.filter(
              (item: Question) => item.key !== key
            ),
            removableCustomTestIdList:
            list?.removableCustomTestIdList&&list?.removableCustomTestIdList?.length > 0
                ? [...list.removableCustomTestIdList, id]
                : [id],
          };
        }
        return list;
      });
      dispatch(dynamicSet(`data_source`, reAssignValues));
    },
    [dispatch, dataSource, record]
  );

  const handleChange = useCallback(
    (key: string, field: keyof Question, value: any) => {
      // const updated_data = data_source.map((item: Question) =>
      //   item.key === key ? { ...item, [field]: value } : item
      // );
      let reAssignValues = dataSource.map((list: ParentQuestion) => {
        if (list.id.includes(record?.id)) {
          return {
            ...list,
            customTestList: list?.customTestList?.map((item: Question) =>
              item.key === key ? { ...item, [field]: value } : item
            ),
          };
        }
        return list;
      });
      dispatch(dynamicSet(`data_source`, reAssignValues));
    },
    [dispatch, dataSource, record]
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        const parentRecord = dataSource.find((record: ParentQuestion) =>
          record.customTestList?.some(
            (question: Question) => question.key === active.id
          )
        );

        if (parentRecord) {
          const activeIndex = parentRecord.customTestList.findIndex(
            (question: Question) => question.key === active.id
          );
          const overIndex = parentRecord.customTestList.findIndex(
            (question: Question) => question.key === over?.id
          );

          const updatedQuestions = arrayMove(
            parentRecord.customTestList,
            activeIndex,
            overIndex
          );

          // Update the parent record with the reordered questions
          const updatedDataSource = dataSource.map(
            (record: ParentQuestion) => {
              if (record.id === parentRecord.id) {
                return {
                  ...record,
                  customTestList: updatedQuestions,
                };
              }
              return record;
            }
          );

          dispatch(dynamicSet(`data_source`, updatedDataSource));
        } else {
          const activeIndex = dataSource.findIndex(
            (record: Question) => record.key === active.id
          );
          const overIndex = dataSource.findIndex(
            (record: Question) => record.key === over?.id
          );

          const updatedData = arrayMove(dataSource, activeIndex, overIndex);
          dispatch(dynamicSet(`data_source`, updatedData));
        }
      }
    },
    [dispatch, dataSource]
  );

  return (
    <div className="sub-question-main-container">
      <div
        className="level-title-container sub-question-header-container"
        style={{ backgroundColor: "transparent" }}
      >
        <h4>{t("questions")}</h4>
        <Button onClick={handleAdd} icon={<IoMdAddCircle size={20} />} />
      </div>
      <Row
        gutter={24}
        justify={"space-between"}
        style={{
          marginBottom: "10px",
          width: "80%",
          padding: "10px",
          marginLeft: "20px",
          backgroundColor: "white",
          borderRadius: "10px",
        }}
        align={"middle"}
      >
        <Col span={1}></Col>
        <Col span={2}>
          <b>{t("s_no")}</b>
        </Col>
        <Col span={7}>
          <b>{t("name")}</b>
        </Col>
        <Col span={6}>
          <b>{t("short_question")}</b>
        </Col>
        <Col
          span={7}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          <b>{t("type")}</b>
        </Col>
      </Row>
      {/* <div className="sub-question-container">
        <div className="sub-question-sno-label text-center">
          <span>{t("s_no")}</span>
        </div>
        <div className="sub-question-name-label text-center">
          <span>{t("name")}</span>
        </div>
        <div className="sub-question-mandatory-label text-center">
          <span>{t("type")}</span>
        </div>
      </div> */}
      {record?.customTestList?.length > 0 && (
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={record?.customTestList?.map((item: Question) => item?.key)}
            strategy={verticalListSortingStrategy}
          >
            {record?.customTestList?.map(
              (record: Question, index: number) => (
                <SortableRow key={record.key} data-row-key={record.key}>
                  <Row
                    gutter={24}
                    justify={"space-between"}
                    style={{
                      marginBottom: "10px",
                      width: "80%",
                      padding: "10px",
                      marginLeft: "20px",
                      backgroundColor: "white",
                      borderRadius: "10px",
                    }}
                    align={"middle"}
                  >
                    {/* <div
                    style={{
                      marginBottom: "10px",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "90%",
                    }}
                  > */}
                    <Col span={1}>
                      <DragHandle />
                    </Col>
                    <Col span={2}>
                      <b>{index + 1}</b>
                    </Col>
                    <Col span={7}>
                      <Input
                        value={record.name}
                        onChange={(e) =>
                          handleChange(record.key, "name", e.target.value)
                        }
                      />
                    </Col>
                    <Col span={6}>
                      <Input
                        value={record.shortQuestion}
                        maxLength={SHORT_QUESTION_MAX_LENGTH}
                        showCount
                        placeholder={truncateToShortQuestion(record.name)}
                        onChange={(e) =>
                          handleChange(
                            record.key,
                            "shortQuestion",
                            capShortQuestion(e.target.value)
                          )
                        }
                      />
                    </Col>
                    <Col
                      span={7}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <Select
                        value={record.type}
                        options={questionTypeOptions}
                        onChange={(e) => handleChange(record.key, "type", e)}
                      />
                      {record.type === "select" ||
                      record.type === "checkbox" ? (
                        <div style={{ width: "100%" }}>
                          <Select
                            value={record.optionList}
                            mode="tags"
                            onChange={(e) =>
                              handleChange(
                                record.key,
                                "optionList",
                                e
                              )
                            }
                            style={{ width: "100%" }}
                            open={false}
                            suffixIcon={null}
                            placeholder={
                              startCase(record?.type) + " " + "Options"
                            }
                          />
                        </div>
                      ) : null}
                    </Col>
                    <Col span={1}>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemove(record.key,record.id)}
                      />
                    </Col>
                  </Row>
                  {/* </div> */}
                  <Divider />
                </SortableRow>
              )
            )}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default React.memo(CustomQuestion);
